import { useTrackBackendEvent } from '@pm/analytics';
import { useGetMeQuery } from '@pm/graphql';
import type { IconName } from '@pm/ui';
import { Button, Card, Icon, Text } from '@pm/ui';
import clsx from 'clsx';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { usePurposemedFlags } from '../../../components/FeatureFlag/FeatureFlag';
import { usePathWithParams } from '../../../hooks/navigation/use-path-with-params';
import { getAdditionalConditionFlowLink } from './additional_condition_card_utils';
import type { AdditionalConditionItem } from './AdditionalConditionsList';
import { ADDITIONAL_CONDITION_STATES } from './types';

export enum AdditionalConditionCardVariant {
  AVAILABLE = 'AVAILABLE',
  LOCKED = 'LOCKED',
  STATUS = 'STATUS',
}

export type CardVariationContent = {
  variant: AdditionalConditionCardVariant;
  description: string;
  icon: IconName;
  ctaLabel?: string;
};

type CardViewProps = {
  content: CardVariationContent;
  title: string;
  isLocked: boolean;
  isAvailable: boolean;
  onAvailableClick: () => void;
};

type AdditionalConditionCardProps = {
  condition: AdditionalConditionItem;
};

function getCardContent(
  condition: AdditionalConditionItem,
  t: TFunction<'treatments', 'AdditionalTreatmentsList'>,
): CardVariationContent {
  const description = t(`Treatments.${condition.conditionName}.Description`);

  switch (condition.state) {
    case ADDITIONAL_CONDITION_STATES.AVAILABLE:
      return {
        variant: AdditionalConditionCardVariant.AVAILABLE,
        description,
        ctaLabel: t('CardStateCTAs.Available'),
        icon: 'arrow_forward',
      };
    case ADDITIONAL_CONDITION_STATES.AVAILABLE_FOR_REFILL:
      return {
        variant: AdditionalConditionCardVariant.AVAILABLE,
        description,
        ctaLabel: t('CardStateCTAs.RefillAvailable'),
        icon: 'arrow_forward',
      };
    case ADDITIONAL_CONDITION_STATES.LOCKED:
      return {
        variant: AdditionalConditionCardVariant.LOCKED,
        description: t('CardStateDescriptions.Locked'),
        icon: 'lock',
      };
    case ADDITIONAL_CONDITION_STATES.LOCKED_BEFORE_CONSULT:
      return {
        variant: AdditionalConditionCardVariant.LOCKED,
        description: t('CardStateDescriptions.LockedBeforeConsult'),
        icon: 'lock',
      };
    case ADDITIONAL_CONDITION_STATES.LOCKED_WAITING_REFILL:
      return {
        variant: AdditionalConditionCardVariant.LOCKED,
        description: t('CardStateDescriptions.LockedWaitingRefill'),
        icon: 'lock',
      };
    case ADDITIONAL_CONDITION_STATES.REQUEST_SUBMITTED:
      return {
        variant: AdditionalConditionCardVariant.STATUS,
        description: t('CardStateDescriptions.RequestSubmitted'),
        ctaLabel: t('CardStateCTAs.RequestSubmitted'),
        icon: 'check',
      };
    default:
      return {
        variant: AdditionalConditionCardVariant.STATUS,
        description: t('CardStateDescriptions.PrescriptionIssued'),
        ctaLabel: t('CardStateCTAs.PrescriptionIssued'),
        icon: 'check',
      };
  }
}

function CardTitle({ title, isLocked }: CardViewProps) {
  return (
    <Text
      size="headingXS"
      className={isLocked ? 'text-content-disabled' : undefined}
    >
      {title}
    </Text>
  );
}

function CardDescription({ content, isLocked }: CardViewProps) {
  if (!content.description) return null;

  if (isLocked) {
    return (
      <div className="flex items-center gap-xxs pt-xxs">
        <Text size="bodyS" className="text-content-disabled">
          {content.description}
        </Text>
        <Icon name={content.icon} size="small" color="text-content-disabled" />
      </div>
    );
  }

  return (
    <div className="pt-xxs">
      <Text size="bodyS" className="text-content-subdued">
        {content.description}
      </Text>
    </div>
  );
}

function CardCta({
  content,
  isAvailable,
  isLocked,
  onAvailableClick,
}: CardViewProps) {
  if (!content.ctaLabel) return null;

  if (isAvailable) {
    return (
      <div className="flex items-center gap-xxs pt-xxs">
        <Button
          type="button"
          intent="tertiary"
          width="fit"
          size="xSmall"
          trailingIcon={<Icon name={content.icon} size="medium" />}
          onClick={onAvailableClick}
        >
          <Text size="bodyS" as="span">
            {content.ctaLabel}
          </Text>
        </Button>
      </div>
    );
  }

  const iconColor = isLocked ? 'text-content-disabled' : 'text-info';

  return (
    <div className="flex items-center gap-xxs pt-xxs">
      <Text
        size="bodyS"
        as="span"
        className={clsx({
          'text-content-disabled': isLocked,
          'text-button-s text-info': !isLocked,
        })}
      >
        {content.ctaLabel}
      </Text>
      <Icon name={content.icon} size="small" color={iconColor} />
    </div>
  );
}

export function AdditionalConditionCard({
  condition,
}: AdditionalConditionCardProps) {
  const { t } = useTranslation('treatments', {
    keyPrefix: 'AdditionalTreatmentsList',
  });
  const navigate = useNavigate();
  const buildPathWithParams = usePathWithParams();
  const { data: meData } = useGetMeQuery();
  const { trackBackendEvent } = useTrackBackendEvent({
    userId: meData?.me?.id,
  });

  const {
    additionalTreatmentsCoverPage,
    additionalTreatmentsCoverPageRefills,
  } = usePurposemedFlags();

  const content = getCardContent(condition, t);
  const isRefill =
    condition.state === ADDITIONAL_CONDITION_STATES.AVAILABLE_FOR_REFILL;
  const coverPageEnabled = isRefill
    ? additionalTreatmentsCoverPageRefills
    : additionalTreatmentsCoverPage;

  const handleAvailableCtaClick = () => {
    if (!coverPageEnabled) {
      navigate(
        buildPathWithParams({
          to:
            getAdditionalConditionFlowLink(
              condition.conditionName,
              'prep',
              isRefill,
            ) ?? '/',
        }),
      );
    } else {
      trackBackendEvent(
        'Additional Treatment Cover Page',
        {
          treatment_name: condition.conditionName,
          is_refill: isRefill,
        },
        { braze: true },
      );
      navigate(
        buildPathWithParams({
          to: `/additional-treatment-start?treatment=${condition.conditionName}&action=${
            isRefill ? 'refill' : 'intake'
          }`,
        }),
      );
    }
  };

  const viewProps: CardViewProps = {
    content,
    title: t(`Treatments.${condition.conditionName}.Title`),
    isLocked: content.variant === AdditionalConditionCardVariant.LOCKED,
    isAvailable: content.variant === AdditionalConditionCardVariant.AVAILABLE,
    onAvailableClick: handleAvailableCtaClick,
  };

  return (
    <Card
      size="small"
      icon={
        <img
          className="h-8 w-8"
          src={`/images/treatment-icons/${condition.conditionName}.svg`}
          alt=""
          aria-hidden
        />
      }
    >
      <CardTitle {...viewProps} />
      <CardDescription {...viewProps} />
      <CardCta {...viewProps} />
    </Card>
  );
}
